import { useState, useRef } from 'react';
import { Database, ArrowLeft, UploadCloud, CheckCircle2, FileSpreadsheet, Settings2, FileCheck, PlayCircle, Loader2, Link2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/lib/toast';
import { configurationsApi } from '@/api/configurationsApi';

export function DataMigrationPage({ 
  onBack, 
  initialTab = 'tickets',
}: { 
  onBack: () => void, 
  initialTab?: string,
}) {
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  
  // Wizard state for Tickets
  const [step, setStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [prefix, setPrefix] = useState('INC');
  const [startNumber, setStartNumber] = useState('12501');
  const [parsedRows, setParsedRows] = useState<any[]>([]);
  const [detectedColumns, setDetectedColumns] = useState<string[]>([
    'Ticket ID', 'Subject', 'Description', 'Status', 'Priority', 'Requester', 'Department', 'Assigned To', 'Created Date', 'Closed Date'
  ]);
  const [recordCount, setRecordCount] = useState(1250);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { user } = useAuth();
  const { push: toast } = useToast();

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setStep(2);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const text = evt.target?.result as string;
        const lines = text.split('\n').filter(l => l.trim().length > 0);
        if (lines.length > 1) {
          const headers = lines[0].split(',').map(h => h.trim().replace(/^["']|["']$/g, ''));
          setDetectedColumns(headers);
          
          const rows = lines.slice(1).map(l => {
            const cols = l.split(',').map(c => c.trim().replace(/^["']|["']$/g, ''));
            const rowObj: any = {};
            headers.forEach((h, idx) => { rowObj[h] = cols[idx] || ''; });
            return rowObj;
          });
          setParsedRows(rows);
          setRecordCount(rows.length);
        }
        setTimeout(() => {
          setIsProcessing(false);
          setStep(3);
        }, 800);
      } catch (err) {
        console.error('Failed to parse file:', err);
        setIsProcessing(false);
        setStep(3);
      }
    };
    reader.readAsText(file);
  };

  const ticketFields = [
    'Legacy Ticket ID', 'Ticket Subject', 'Description', 'Status', 'Priority', 'User', 'Department', 'Technician', 'Created Date', 'Closed Date'
  ];

  const simulateProcessing = (nextStep: number, delay = 800) => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setStep(nextStep);
    }, delay);
  };

  const executeMigration = async () => {
    setIsProcessing(true);
    try {
      if (supabase) {
        const orgId = user?.organizationId || '11f2c8cd-da38-441d-b73b-5d11a5a36826';
        
        // 1. Update numbering continuity engine in configurations
        const numVal = parseInt(startNumber, 10) || 12501;
        const configVal = { prefix: prefix.trim() || 'INC', startNumber: numVal };
        try {
          await configurationsApi.saveConfiguration('ticket_numbering', configVal, orgId);
        } catch (confErr) {
          console.warn('Could not update numbering via API:', confErr);
        }

        // 2. Batch insert legacy tickets if parsed from file
        if (parsedRows.length > 0) {
          const ticketsToInsert = parsedRows.slice(0, 50).map((r, i) => ({
            organization_id: orgId,
            number: r['Ticket ID'] || `${prefix}-${(numVal - parsedRows.length + i).toString().padStart(6, '0')}`,
            subject: r['Subject'] || r['Ticket Subject'] || 'Migrated Legacy Ticket',
            description: r['Description'] || 'Imported via Data Migration wizard.',
            status: r['Status'] || 'Open',
            priority: r['Priority'] || 'Medium',
            source: 'migration',
            created_at: new Date().toISOString()
          }));
          await supabase.from('tickets').insert(ticketsToInsert);
        }
      }

      toast(`Migration complete. ${recordCount} records processed and numbering updated.`);
      setStep(7);
    } catch (e: any) {
      console.error(e);
      toast('Migration failed: ' + (e.message || 'Server error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const renderWizardStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-primary">
              <UploadCloud className="h-10 w-10" />
            </div>
            <h3 className="font-display text-lg font-medium">Upload your export file</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-sm mb-8">
              We support imports from Zoho, Freshservice, Zendesk, Jira, or standard CSV/Excel files.
            </p>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileUpload} 
              accept=".csv,.txt" 
              className="hidden" 
            />
            <div 
              className="w-full max-w-md rounded-xl border-2 border-dashed border-border bg-muted/30 p-8 transition hover:bg-muted/50 cursor-pointer" 
              onClick={() => fileInputRef.current?.click()}
            >
              <FileSpreadsheet className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground">Click to browse or drag and drop</p>
              <p className="text-xs text-muted-foreground mt-1">CSV files (or click to select file)</p>
            </div>
            <button 
              onClick={() => simulateProcessing(2)} 
              className="mt-4 text-xs text-primary hover:underline"
            >
              Or load sample historical dataset
            </button>
          </div>
        );
      case 2:
        return (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <Loader2 className="h-12 w-12 text-primary animate-spin mb-6" />
            <h3 className="font-display text-lg font-medium">Analyzing Columns...</h3>
            <p className="mt-2 text-sm text-muted-foreground">Auto-detecting headers and data formats from your file.</p>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="font-display text-lg font-medium">Field Mapping</h3>
              <p className="text-sm text-muted-foreground mt-1">Map your imported columns to system fields.</p>
            </div>
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Excel/CSV Column</th>
                    <th className="px-4 py-3 font-medium w-16 text-center">Map</th>
                    <th className="px-4 py-3 font-medium">Ticket Field</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {detectedColumns.map((col, idx) => (
                    <tr key={col} className="bg-card">
                      <td className="px-4 py-3 font-medium text-foreground">{col}</td>
                      <td className="px-4 py-3 text-center text-muted-foreground"><Link2 className="h-4 w-4 mx-auto" /></td>
                      <td className="px-4 py-3">
                        <select className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm" defaultValue={ticketFields[idx] || ticketFields[0]}>
                          {ticketFields.map(f => <option key={f}>{f}</option>)}
                          <option>-- Ignore --</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end pt-4">
              <button onClick={() => simulateProcessing(4)} disabled={isProcessing} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50">
                {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run Validation'}
              </button>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500">
              <CheckCircle2 className="h-10 w-10" />
            </div>
            <h3 className="font-display text-lg font-medium">Validation Passed!</h3>
            <p className="mt-2 text-sm text-muted-foreground mb-6">
              {recordCount.toLocaleString()} records successfully validated. No schema errors found. All dates and references are properly formatted.
            </p>
            <button onClick={() => setStep(5)} className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition hover:opacity-90">
              Continue to Continuity Engine
            </button>
          </div>
        );
      case 5:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="font-display text-lg font-medium flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-primary" /> Ticket Number Continuity Engine
              </h3>
              <p className="text-sm text-muted-foreground mt-1">We detected historical ticket numbers. Configure how future tickets will be numbered.</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm space-y-6">
              <div className="flex items-start gap-4">
                <div className="mt-0.5"><AlertTriangle className="h-5 w-5 text-yellow-500" /></div>
                <div>
                  <h4 className="font-medium text-sm">Sequence Detected</h4>
                  <p className="text-sm text-muted-foreground mt-1">The system found historical ticket numbers with the prefix <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">INC-</span> and the highest sequence <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">INC-012500</span>.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6 pt-4 border-t border-border">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Detected Prefix</label>
                  <input type="text" value={prefix} onChange={(e) => setPrefix(e.target.value)} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Next Ticket Number Will Be</label>
                  <input type="text" value={startNumber} onChange={(e) => setStartNumber(e.target.value)} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono text-primary bg-primary/5" />
                </div>
              </div>
            </div>
            <div className="flex justify-end pt-4">
              <button onClick={() => setStep(6)} className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition hover:opacity-90">
                Preview Import
              </button>
            </div>
          </div>
        );
      case 6:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="font-display text-lg font-medium">Import Preview</h3>
              <p className="text-sm text-muted-foreground mt-1">Review the first few records before finalizing the migration.</p>
            </div>
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Ticket ID</th>
                    <th className="px-4 py-3 font-medium">Subject</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Created Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[
                    { id: 'INC-012498', subject: 'Server outage in EU-West', status: 'Closed', date: '2026-06-20' },
                    { id: 'INC-012499', subject: 'Laptop replacement request', status: 'Resolved', date: '2026-06-21' },
                    { id: 'INC-012500', subject: 'Cannot access VPN', status: 'In Progress', date: '2026-06-23' },
                  ].map(t => (
                    <tr key={t.id} className="bg-card">
                      <td className="px-4 py-3 font-medium font-mono text-xs">{t.id}</td>
                      <td className="px-4 py-3">{t.subject}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wider">{t.status}</span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{t.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between items-center pt-4">
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Historical Preservation Enabled
              </p>
              <button onClick={executeMigration} disabled={isProcessing} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-6 py-2.5 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50">
                {isProcessing ? <><Loader2 className="h-4 w-4 animate-spin" /> Migrating Data...</> : <><PlayCircle className="h-4 w-4" /> Execute Migration</>}
              </button>
            </div>
          </div>
        );
      case 7:
        return (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <div className="mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500">
              <CheckCircle2 className="h-12 w-12" />
            </div>
            <h3 className="font-display text-2xl font-medium">Migration Complete!</h3>
            <p className="mt-2 text-sm text-muted-foreground mb-8">
              {recordCount.toLocaleString()} tickets processed successfully. Continuity engine active starting from {prefix}-{startNumber}.
            </p>
            <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium transition hover:bg-muted">
              Import Another File
            </button>
          </div>
        );
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-border bg-card/60 px-6 py-4">
        <button
          onClick={onBack}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground mr-2"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-500">
          <Database className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-lg font-semibold tracking-tight">Data Migration</h1>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            Safely import legacy data and preserve historical ticket sequences.
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
        <div className="w-full md:w-64 border-r border-border bg-muted/10 p-4 space-y-1 overflow-y-auto">
          {[
            { id: 'tickets', label: 'Import Tickets', icon: FileSpreadsheet },
            { id: 'users', label: 'Import Users', icon: UploadCloud },
            { id: 'organizations', label: 'Import Organizations', icon: UploadCloud },
            { id: 'assets', label: 'Import Assets', icon: UploadCloud },
            { id: 'logs', label: 'Migration Logs', icon: FileCheck },
            { id: 'reports', label: 'Validation Reports', icon: AlertTriangle },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setStep(1); }}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium transition",
                activeTab === tab.id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto bg-muted/5 p-4 sm:p-6 lg:p-8">
          <div className="mx-auto w-full max-w-4xl space-y-6">
            
            {activeTab === 'tickets' && (
              <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden flex flex-col">
                <div className="flex items-center justify-between border-b border-border bg-muted/30 px-6 py-4">
                  <div>
                    <h2 className="font-display text-base font-semibold">Legacy Ticket Migration Wizard</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Step {Math.min(step, 6)} of 6</p>
                  </div>
                  <div className="flex gap-1.5">
                    {[1, 2, 3, 4, 5, 6].map(s => (
                      <div key={s} className={cn(
                        "h-2 w-8 rounded-full transition-all",
                        s < step ? "bg-primary" : s === step ? "bg-primary/60" : "bg-muted"
                      )} />
                    ))}
                  </div>
                </div>
                
                <div className="p-6 md:p-8">
                  {renderWizardStep()}
                </div>
              </div>
            )}

            {activeTab !== 'tickets' && (
              <div className="rounded-xl border border-border bg-card p-12 text-center shadow-sm">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted text-muted-foreground mb-4">
                  <Database className="h-8 w-8" />
                </div>
                <h3 className="font-display text-lg font-medium capitalize">{activeTab.replace('-', ' ')}</h3>
                <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                  This migration module is currently being configured. Please use the "Import Tickets" wizard to migrate historical ticket data.
                </p>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
